import { AlertTriangle, TrendingDown, Clock, Sparkles } from "lucide-react";

const ProblemSection = () => {
  const painPoints = [
    {
      icon: AlertTriangle,
      title: "Generic Content",
      description: "Your brand voice gets lost in templated content that could belong to anyone in your industry.",
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      borderColor: "border-destructive/20"
    },
    {
      icon: Clock,
      title: "The Dependency Trap",
      description: "Every campaign, every post, every strategy decision requires going through your agency. You're not building capability, you're renting it.",
      color: "text-amber-400",
      bgColor: "bg-amber-400/10",
      borderColor: "border-amber-400/20"
    },
    {
      icon: TrendingDown,
      title: "Zero In-House Capability",
      description: "When the retainer ends, so does your content engine. You're left with no systems, no knowledge, and no team.",
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      borderColor: "border-destructive/20"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 md:py-32 bg-surface-dark relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-surface-dark via-background/50 to-surface-dark" />
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-destructive/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl" />
      
      <div className="container relative z-10">
        <div className="max-w-5xl mx-auto text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 glass-card rounded-full border border-destructive/20">
            <Sparkles className="w-4 h-4 text-destructive" />
            <span className="text-sm font-medium text-destructive">The Problem</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold font-display mb-8 leading-tight text-foreground">
            You're Spending{" "}
            <span className="text-destructive font-extrabold">₹30 Lakhs a Year</span>{" "}
            <br className="hidden md:block" />
            to be Misunderstood.
          </h2>
          
          <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed max-w-4xl mx-auto">
            Traditional agencies keep you dependent while your competition builds their own content powerhouse
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 md:gap-10 mb-16 md:mb-20">
          {painPoints.map((point, index) => (
            <div key={index} className="group relative">
              <div className={`relative p-8 md:p-10 rounded-2xl ${point.bgColor} border ${point.borderColor} hover-lift transition-all duration-500 overflow-hidden h-full`}>
                {/* Decorative background pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-current rounded-full blur-2xl" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-current rounded-full blur-xl" />
                </div>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 ${point.bgColor} border ${point.borderColor} rounded-2xl mb-6 group-hover:scale-110 transition-all duration-300`}>
                    <point.icon className={`w-8 h-8 md:w-10 md:h-10 ${point.color}`} />
                  </div>
                  
                  {/* Content */}
                  <h3 className={`text-xl md:text-2xl font-bold mb-4 font-display ${point.color}`}>
                    {point.title}
                  </h3>
                  
                  <p className="text-foreground/90 leading-relaxed text-base md:text-lg font-sans">
                    {point.description}
                  </p>
                  
                  {/* Problem indicator */}
                  <div className="absolute top-4 right-4 w-3 h-3 bg-destructive rounded-full animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 md:p-12 glass-card rounded-2xl border border-destructive/20 overflow-hidden">
            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-destructive/5 via-transparent to-amber-400/5" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-destructive/10 rounded-full border border-destructive/20">
                <TrendingDown className="w-4 h-4 text-destructive" />
                <span className="text-sm font-medium text-destructive">Cash Burn Reality</span>
              </div>
              
              <p className="text-xl md:text-2xl font-sans italic text-foreground/90 leading-relaxed">
                This isn't a marketing strategy.{" "}
                <span className="text-destructive font-semibold">
                  It's a cash burn with temporary results.
                </span>
              </p>
              
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 pt-8 border-t border-border">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-destructive mb-1 font-display">₹30L+</div>
                  <div className="text-sm text-foreground/70 font-sans">Annual Spend</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-destructive mb-1 font-display">0%</div>
                  <div className="text-sm text-foreground/70 font-sans">Assets Owned</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-destructive mb-1 font-display">100%</div>
                  <div className="text-sm text-foreground/70 font-sans">Dependency</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-destructive mb-1 font-display">∞</div>
                  <div className="text-sm text-foreground/70 font-sans">Contract Cycle</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;